import { RRule } from 'rrule'
import { Temporal } from 'temporal-polyfill'

import type { ScheduleEventCreate, ScheduleEventUpdate } from '../types'

const formatDurationAsHours = (milliseconds: number): string => {
  const totalMinutes = Math.round(milliseconds / 60000)
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60

  return minutes === 0 ? `PT${hours}H` : `PT${hours}H${minutes}M`
}

const jsWeekdayToRRule = (jsWeekday: number): number =>
  jsWeekday === 0 ? 6 : jsWeekday - 1

const createWeeklyRRule = (dayOfWeek: number): string => {
  const rrule = new RRule({
    freq: RRule.WEEKLY,
    byweekday: [jsWeekdayToRRule(dayOfWeek)],
    interval: 1,
    count: 1300,
  })
  const fullString = rrule.toString()
  const rruleMatch = fullString.match(/RRULE:(.+)/)

  return rruleMatch ? rruleMatch[1] : fullString.replace(/^RRULE:/, '')
}

const formatDateTime = (dateTime: Temporal.ZonedDateTime): string => {
  const year = dateTime.year.toString().padStart(4, '0')
  const month = dateTime.month.toString().padStart(2, '0')
  const day = dateTime.day.toString().padStart(2, '0')
  const hour = dateTime.hour.toString().padStart(2, '0')
  const minute = dateTime.minute.toString().padStart(2, '0')
  const second = dateTime.second.toString().padStart(2, '0')

  return `${year}${month}${day}T${hour}${minute}${second}`
}

const weeklyHoursTiming = (
  start: Date,
  end: Date,
  scheduleTimeZone: string,
) => {
  const dayOfWeek = start.getDay()
  const baseDate = new Date('2012-01-01T00:00:00')
  baseDate.setDate(baseDate.getDate() + dayOfWeek)
  baseDate.setHours(start.getHours(), start.getMinutes(), 0, 0)

  const zonedStart = Temporal.ZonedDateTime.from({
    year: baseDate.getFullYear(),
    month: baseDate.getMonth() + 1,
    day: baseDate.getDate(),
    hour: baseDate.getHours(),
    minute: baseDate.getMinutes(),
    timeZone: scheduleTimeZone,
  })

  return {
    dtstart: formatDateTime(zonedStart),
    duration: formatDurationAsHours(end.getTime() - start.getTime()),
    rrule: createWeeklyRRule(dayOfWeek),
    switchboard_routing: 'OPEN' as const,
    ui_component: 'WEEKLY_HOURS' as const,
    summary: 'Open',
  }
}

export const createWeeklyHoursEvent = (
  start: Date,
  end: Date,
  scheduleTimeZone: string,
): ScheduleEventCreate => ({
  ...weeklyHoursTiming(start, end, scheduleTimeZone),
  fbtype: 'FREE',
  time_zone: scheduleTimeZone,
})

export const updateWeeklyHoursEvent = (
  start: Date,
  end: Date,
  scheduleTimeZone: string,
): ScheduleEventUpdate => weeklyHoursTiming(start, end, scheduleTimeZone)
